/**
 * What one type brings, opened under its row on the item page (Planning &
 * delivery → How it will be done). A work type shows how that kind of work is
 * approached (its own row, canon 111) and then its pattern; any other type shows
 * its description and its pattern (certainty, shape, workload, bar).
 */

import { Link } from 'react-router-dom';
import { LABEL_MONO } from '@/components/mono/primitives';
import { ExecutionRecipePanel } from '@/components/prioritization/item-view/ExecutionRecipePanel';
import { useTypeListPickers } from '@/hooks/prioritization/useTypeListPickers';
import { useTypePatternsStore } from '@/stores/prioritization/typePatternsStore';
import { resourceTypeLabel } from '@/stores/prioritization/resourceTypesStore';
import {
  ANCHOR_LABEL,
  DATE_FIRMNESS_LABEL,
  QUALITY_LABEL,
  defaultAnchorFor,
  isEmptyPattern,
} from '@/lib/typePatterns';

function Line({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="grid grid-cols-1 gap-x-4 py-1.5 sm:grid-cols-[9rem_minmax(0,1fr)]">
      <span className={LABEL_MONO}>{label}</span>
      <div className="text-sm text-[var(--text-sec)]">{children}</div>
    </div>
  );
}

export function TypePatternDetail({
  listKey,
  value,
  workspaceId,
}: {
  listKey: string;
  value: string;
  workspaceId: string | null;
}) {
  const { lists } = useTypeListPickers(workspaceId);
  const pattern = useTypePatternsStore((s) => s.patternFor(listKey, value));
  useTypePatternsStore((s) => s.patterns);
  const list = lists.find((l) => l.key === listKey);
  const option = list?.options.find((o) => o.value === value);
  const anchor = (a?: 'start' | 'date') => ANCHOR_LABEL[a ?? defaultAnchorFor(listKey)];

  return (
    <div className="border border-[var(--rule)] bg-[var(--surface)] px-4 py-3" data-testid={`type-detail-${listKey}`}>
      {listKey === 'work_type' ? (
        <ExecutionRecipePanel workType={value} />
      ) : (
        option?.description && <p className="mb-2 text-sm text-[var(--text-sec)]">{option.description}</p>
      )}

      {!pattern || isEmptyPattern(pattern) ? (
        <p className="mt-2 text-sm text-[var(--text-ter)]">
          {option?.label ?? value} has no pattern yet.{' '}
          <Link to="/prioritization/settings/type-patterns" className="underline">Type Lists &amp; Patterns</Link>
        </p>
      ) : (
        <div className="mt-2 divide-y divide-[var(--rule)]">
          <p className="pb-1.5 text-xs text-[var(--text-ter)]">
            Pattern version {pattern.versionNumber}
            {pattern.reason ? ` — ${pattern.reason}` : ''}
          </p>
          {(pattern.certainty.scheduleSlackWeeks !== undefined ||
            pattern.certainty.confidenceCeiling !== undefined ||
            pattern.certainty.approach ||
            pattern.certainty.effortMultiplier) && (
            <Line label="Certainty">
              {[
                pattern.certainty.approach && `${pattern.certainty.approach} approach`,
                pattern.certainty.effortMultiplier && `effort ×${pattern.certainty.effortMultiplier}`,
                pattern.certainty.scheduleSlackWeeks !== undefined && `${pattern.certainty.scheduleSlackWeeks} weeks of slack`,
                pattern.certainty.confidenceCeiling !== undefined &&
                  `confidence up to ${Math.round(pattern.certainty.confidenceCeiling * 100)}%`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Line>
          )}
          {pattern.certainty.beforeScheduling?.length ? (
            <Line label="Before scheduling">
              <ul className="list-disc pl-4">
                {pattern.certainty.beforeScheduling.map((b) => <li key={b}>{b}</li>)}
              </ul>
            </Line>
          ) : null}
          {pattern.shape.stages?.length ? (
            <Line label="Shape">
              {pattern.shape.stages.map((s) => `${s.label} (${s.weeks} wk)`).join(' → ')}
              <span className="ml-2 text-xs text-[var(--text-ter)]">from {anchor(pattern.shape.anchor)}</span>
            </Line>
          ) : null}
          {pattern.workload.lines?.length ? (
            <Line label="Workload">
              {pattern.workload.lines.map((l, i) => (
                <div key={i}>
                  {resourceTypeLabel(l.resourceType)}: {l.capacityUnits} CU, weeks {l.fromWeek} to {l.toWeek}
                </div>
              ))}
              <span className="text-xs text-[var(--text-ter)]">weeks from {anchor(pattern.workload.anchor)}</span>
            </Line>
          ) : null}
          {(pattern.bar.date || pattern.bar.quality || pattern.bar.firmness || pattern.bar.signOff?.length) && (
            <Line label="Bar">
              {[
                pattern.bar.date && DATE_FIRMNESS_LABEL[pattern.bar.date],
                pattern.bar.quality && QUALITY_LABEL[pattern.bar.quality],
                pattern.bar.firmness && `slides last ${pattern.bar.firmness}/10`,
                pattern.bar.signOff?.length && `signs off: ${pattern.bar.signOff.join(', ')}`,
              ]
                .filter(Boolean)
                .join(' · ')}
            </Line>
          )}
        </div>
      )}
    </div>
  );
}
